// Profile-embeddable card that lets the user rotate their quick-unlock PIN,
// remove it, and clear the biometric credential — WITHOUT touching the
// saved credentials (identifier + password remain in place so quick-login
// still auto-detects them on next visit).
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { KeyRound, Fingerprint, ShieldCheck, Trash2, Check, X } from "lucide-react";
import {
  hasPin, setPin, verifyPin, clearPin,
  bioAvailable, hasBio, clearBio,
} from "@/utils/quickLogin";

export default function QuickLoginSettings() {
  const [pinExists, setPinExists] = useState(hasPin());
  const [bioExists, setBioExists] = useState(hasBio());
  const [bioSupported, setBioSupported] = useState(false);
  const [mode, setMode] = useState(null); // null | "change" | "setup"
  const [curPin, setCurPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [newPin2, setNewPin2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { bioAvailable().then(setBioSupported); }, []);

  const cancel = () => {
    setMode(null); setCurPin(""); setNewPin(""); setNewPin2(""); setErr("");
  };

  const submit = async (e) => {
    e?.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (mode === "change") {
        const ok = await verifyPin(curPin);
        if (!ok) { setErr("PIN الحالي غير صحيح"); return; }
      }
      if (newPin.length < 4) { setErr("PIN الجديد يجب أن يكون 4-6 أرقام"); return; }
      if (newPin !== newPin2) { setErr("رمزا PIN غير متطابقين"); return; }
      await setPin(newPin);
      setPinExists(true);
      toast.success(mode === "change" ? "✓ تم تغيير رمز PIN" : "✓ تم حفظ رمز PIN", { duration: 2500 });
      cancel();
    } catch (ex) { setErr(ex.message || "خطأ"); }
    finally { setBusy(false); }
  };

  const removePin = () => {
    if (!window.confirm("إزالة رمز PIN؟ سيُطلب منك إدخال كلمة المرور في كل مرة.")) return;
    clearPin();
    setPinExists(false);
    toast.success("✓ تمت إزالة PIN", { duration: 2000 });
  };

  const removeBio = () => {
    if (!window.confirm("إلغاء تسجيل البصمة على هذا الجهاز؟")) return;
    clearBio();
    setBioExists(false);
    toast.success("✓ تم إلغاء البصمة", { duration: 2000 });
  };

  return (
    <div className="bg-white rounded-3xl talabya-shadow-soft p-5 space-y-3" data-testid="quick-login-settings">
      <div className="flex items-center gap-2">
        <ShieldCheck size={18} className="text-primary-brand"/>
        <h2 className="font-black text-lg" style={{fontFamily:"Cairo"}}>الدخول السريع</h2>
      </div>
      <p className="text-xs text-[var(--text-secondary)]">
        رمز PIN والبصمة يعملان فقط على هذا الجهاز. تغييرهما لا يؤثر على كلمة المرور.
      </p>

      {/* PIN row */}
      <div className="flex items-center justify-between p-3 rounded-xl bg-[var(--surface-alt)]" data-testid="pin-row">
        <div className="flex items-center gap-2">
          <KeyRound size={16} className="text-primary-brand"/>
          <div>
            <div className="font-bold text-sm">رمز PIN</div>
            <div className="text-[11px] text-[var(--text-secondary)]">
              {pinExists ? "مفعّل — يمكن تغييره في أي وقت" : "غير مفعّل"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {pinExists ? (
            <>
              <button onClick={() => setMode("change")}
                className="px-3 py-1.5 rounded-lg bg-primary-brand text-white text-xs font-black hover:opacity-90"
                data-testid="btn-change-pin">
                تغيير
              </button>
              <button onClick={removePin} title="إزالة PIN"
                className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 flex items-center justify-center"
                data-testid="btn-remove-pin">
                <Trash2 size={14}/>
              </button>
            </>
          ) : (
            <button onClick={() => setMode("setup")}
              className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-black hover:bg-emerald-700"
              data-testid="btn-setup-pin">
              تفعيل
            </button>
          )}
        </div>
      </div>

      {/* Inline PIN change/setup form */}
      {mode && (
        <form onSubmit={submit} className="rounded-xl border border-primary-brand/30 bg-primary-light/50 p-3 space-y-2" data-testid="pin-form">
          <div className="flex items-center justify-between mb-1">
            <div className="text-xs font-black text-primary-brand">
              {mode === "change" ? "تغيير رمز PIN" : "إنشاء رمز PIN"}
            </div>
            <button type="button" onClick={cancel} className="text-[var(--text-secondary)] p-1" data-testid="btn-cancel-pin">
              <X size={14}/>
            </button>
          </div>
          {mode === "change" && (
            <input
              type="password" inputMode="numeric" maxLength={6} value={curPin}
              onChange={(e) => setCurPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="w-full px-3 py-2 rounded-lg border border-[var(--border)] bg-white text-center tracking-[6px] font-black text-lg"
              placeholder="PIN الحالي"
              autoFocus
              data-testid="pin-current"/>
          )}
          <input
            type="password" inputMode="numeric" maxLength={6} value={newPin}
            onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="w-full px-3 py-2 rounded-lg border border-[var(--border)] bg-white text-center tracking-[6px] font-black text-lg"
            placeholder="PIN جديد (4-6 أرقام)"
            autoFocus={mode === "setup"}
            data-testid="pin-new1"/>
          <input
            type="password" inputMode="numeric" maxLength={6} value={newPin2}
            onChange={(e) => setNewPin2(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="w-full px-3 py-2 rounded-lg border border-[var(--border)] bg-white text-center tracking-[6px] font-black text-lg"
            placeholder="أعد إدخاله للتأكيد"
            data-testid="pin-new2"/>
          {err && <div className="text-xs text-red-600 bg-red-50 px-2 py-1.5 rounded" data-testid="pin-err">{err}</div>}
          <button type="submit" disabled={busy || newPin.length < 4}
            className="w-full py-2.5 rounded-lg bg-primary-brand text-white font-black text-sm hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-1.5"
            data-testid="btn-save-pin">
            <Check size={14}/> {busy ? "جارٍ الحفظ..." : "حفظ"}
          </button>
        </form>
      )}

      {/* Biometric row */}
      {bioSupported && (
        <div className="flex items-center justify-between p-3 rounded-xl bg-[var(--surface-alt)]" data-testid="bio-row">
          <div className="flex items-center gap-2">
            <Fingerprint size={16} className="text-primary-brand"/>
            <div>
              <div className="font-bold text-sm">البصمة</div>
              <div className="text-[11px] text-[var(--text-secondary)]">
                {bioExists ? "مسجّلة على هذا الجهاز" : "غير مسجّلة — سجّلها من شاشة تسجيل الدخول"}
              </div>
            </div>
          </div>
          {bioExists && (
            <button onClick={removeBio} title="إلغاء البصمة"
              className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 flex items-center justify-center"
              data-testid="btn-remove-bio">
              <Trash2 size={14}/>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
